import { useState, useCallback } from "react";
import { BillDataEntity, ProcessBillJsonUseCase } from "../../domain";
import { BillRepositoryImpl } from "../../data/repositories/BillRepositoryImpl";

// Create repository instance (singleton)
const billRepository = new BillRepositoryImpl();

// Create use case
const processBillJsonUseCase = new ProcessBillJsonUseCase(billRepository);

export interface UseBillProcessingReturn {
  processing: boolean;
  error: string | null;
  processBillJson: (billData: BillDataEntity) => Promise<boolean>;
  clearError: () => void;
}

export const useBillProcessing = (): UseBillProcessingReturn => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Process bill JSON for GL classification
   */
  const processBillJson = useCallback(
    async (billData: BillDataEntity): Promise<boolean> => {
      setProcessing(true);
      setError(null);

      try {
        const result = await processBillJsonUseCase.execute(billData);

        if (!result.success) {
          throw new Error(result.error || "Failed to process bill");
        }

        return result.success;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to process bill";
        setError(errorMessage);
        console.error("Failed to process bill JSON:", error);
        return false;
      } finally {
        setProcessing(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    processing,
    error,
    processBillJson,
    clearError,
  };
};
